import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { isValidSession } from "./session-schema";

// Auth-State + Storage. sessionStorage (nicht localStorage) — Tab-scoped, weg bei
// Tab-Close. Bewusste Wahl gegen localStorage (XSS-Persistenz), wie admin-ui.
//
// Source-of-Truth: Context-State. sessionStorage ist Hydration-Source beim Mount.

const STORAGE_KEY = "portal-session";

export interface AuthSession {
  accessToken: string;
  expiresAt: number; // epoch ms
  user: {
    id: string;
    email: string;
    role: string;
  };
}

interface AuthContextValue {
  session: AuthSession | null;
  setSession: (s: AuthSession | null) => void;
  logout: () => void;
  isLoading: boolean; // true waehrend Initial-Hydration
}

const AuthContext = createContext<AuthContextValue | null>(null);

function readFromStorage(): AuthSession | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!isValidSession(parsed)) {
      // Schema-Drift oder Manipulation → clean
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    const session = parsed as AuthSession;
    // Wenn schon abgelaufen → ignorieren
    if (session.expiresAt <= Date.now()) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return session;
  } catch {
    // JSON-Parse-Error → clean
    sessionStorage.removeItem(STORAGE_KEY);
    return null;
  }
}

function writeToStorage(session: AuthSession | null): void {
  if (session === null) {
    sessionStorage.removeItem(STORAGE_KEY);
  } else {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  }
}

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [session, setSessionState] = useState<AuthSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Initial-Hydration aus sessionStorage
  useEffect(() => {
    setSessionState(readFromStorage());
    setIsLoading(false);
  }, []);

  const setSession = useCallback((s: AuthSession | null) => {
    writeToStorage(s);
    setSessionState(s);
  }, []);

  const logout = useCallback(() => {
    writeToStorage(null);
    setSessionState(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ session, setSession, logout, isLoading }),
    [session, setSession, logout, isLoading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

// Test-Helper: direkt Storage-Inhalt lesen (z.B. fuer Unit-Tests)
export const __testHelpers = { readFromStorage, writeToStorage, STORAGE_KEY };
